/*
 * Copyright (c) 2026 Centrica.  All rights reserved.
 *
 * This source code is licensed under the license found in the
 * LICENSE file in the root directory of this source tree.
 * Date: 2026-10-29
 */
import type { TranslationService } from '@nebula-shared/data-access';
import type { Observable } from 'rxjs';
import type {
  CatalogoInformes,
  DefinicionInforme,
  EstadoInforme,
  MensajeInforme,
  PestanaInforme,
  TextoInforme,
  VistaPreviaInforme,
} from './informes.model';

/** Id del rango de fechas, comun a todos los informes. */
export const RANGO_FECHA = 'fecha';

/** Claves i18n del contexto generico. */
const T = {
  FECHA_AYUDA: 'COMMON.INFORMES.FECHA.AYUDA',
  FECHA_ORDEN: 'COMMON.INFORMES.FECHA.ORDEN',
  FECHA_FUERA: 'COMMON.INFORMES.FECHA.FUERA_DE_VIGENCIA',
  NUMERO_ORDEN: 'COMMON.INFORMES.RANGO.ORDEN_NUMERO',
  RANGO_FALTA: 'COMMON.INFORMES.RANGO.FALTA',
  DESDE: 'COMMON.INFORMES.RANGO.DESDE_EL_PRIMERO',
  HASTA: 'COMMON.INFORMES.RANGO.HASTA_EL_ULTIMO',
  OBLIGATORIO: 'COMMON.INFORMES.RANGO.OBLIGATORIO',
} as const;

/** Numero de firmas del pie. */
export const FIRMAS_DEL_INFORME = 4;

/**
 * Nombre de un mes para usarlo dentro de una frase, con la ortografia del idioma: en español en
 * minuscula («de enero a marzo»); en ingles, con mayuscula.
 *
 * @param traduccion servicio de traduccion
 * @param mes mes 1..12
 * @returns el nombre del mes
 */
export function mesDentroDeFrase(traduccion: TranslationService, mes: string): string {
  const nombre = traduccion.translate(`BUDGET.RUBRO.MES.${Number(mes) || 1}`);
  return traduccion.lang() === 'es' ? nombre.toLowerCase() : nombre;
}

/**
 * Convierte AAAA-MM-DD en DD/MM/AAAA.
 *
 * @param valor fecha ISO del control de fecha
 * @returns la fecha para mostrar, o vacio
 */
export function fechaParaMostrar(valor: string): string {
  if (!valor) return '';
  const [a, m, d] = valor.split('-');
  return `${d}/${m}/${a}`;
}

/**
 * Contexto generico de un informe: los parametros con que abre, sus validaciones y su vista
 * previa. Es la base de la jerarquia: cada producto lo extiende con lo comun a sus informes y un
 * informe con reglas propias lo extiende de nuevo (contexto especifico), de modo que las
 * validaciones quedan en un solo lugar por nivel.
 *
 * El contexto se crea en el contexto de inyeccion del lanzador: puede usar `inject()` en su
 * constructor (p. ej. para el selector de terceros).
 */
export class ContextoInforme {
  /**
   * @param definicion informe al que pertenece el contexto
   * @param catalogo catalogo del producto
   */
  constructor(
    readonly definicion: DefinicionInforme,
    readonly catalogo: CatalogoInformes,
  ) {}

  /**
   * Parametros con que abre el lanzador o con que se restablece: la vigencia en curso, del 1 de
   * enero a hoy, y los valores iniciales del catalogo.
   *
   * @param hoy fecha de referencia
   * @returns el estado inicial
   */
  estadoInicial(hoy: Date = new Date()): EstadoInforme {
    const c = this.catalogo;
    const anio = String(hoy.getFullYear());
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const rangos: EstadoInforme['rangos'] = {
      [RANGO_FECHA]: { inicial: `${anio}-01-01`, final: iso(hoy) },
    };
    for (const r of c.rangos ?? []) rangos[r.id] = { inicial: '', final: '' };
    return {
      selector: c.selector?.inicial ?? null,
      interruptor: false,
      vigencia: anio,
      rangos,
      campos: Object.fromEntries((c.campos ?? []).map((x) => [x.id, ''])),
      valorMinimo: '',
      selecciones: Object.fromEntries((c.selecciones ?? []).map((x) => [x.id, [...x.inicial]])),
      opciones: Object.fromEntries((c.opciones ?? []).map((x) => [x.id, x.inicial])),
      mes: String(hoy.getMonth() + 1),
      niveles: [...(c.nivelesIniciales ?? [])],
      datosNivel: {},
      firmas: Array.from({ length: FIRMAS_DEL_INFORME }, () => ({ nombre: '', cargo: '' })),
      formato: 'pdf',
    };
  }

  /**
   * Si el rango es obligatorio en este informe. La fecha siempre lo es; un contexto especifico
   * puede exigir otros.
   *
   * @param id id del rango
   * @returns si el valor inicial es obligatorio
   */
  rangoObligatorio(id: string): boolean {
    return id === RANGO_FECHA;
  }

  /**
   * Etiqueta del rango en este informe (un contexto especifico puede cambiarla).
   *
   * @param id id del rango
   * @returns la clave i18n de la etiqueta
   */
  etiquetaRango(id: string): string {
    return this.catalogo.rangos?.find((r) => r.id === id)?.etiqueta ?? 'COLUMNS.DATE';
  }

  /**
   * Placeholders del rango.
   *
   * @param id id del rango
   * @returns las claves i18n de los placeholders inicial y final
   */
  placeholdersRango(id: string): { inicial: string; final: string } {
    return this.rangoObligatorio(id)
      ? { inicial: T.OBLIGATORIO, final: T.HASTA }
      : { inicial: T.DESDE, final: T.HASTA };
  }

  /**
   * Mensaje bajo un rango: el error que bloquea, el aviso o la ayuda.
   *
   * @param id id del rango
   * @param estado parametros actuales
   * @returns el mensaje del rango
   */
  mensajeRango(id: string, estado: EstadoInforme): MensajeInforme {
    const v = estado.rangos[id] ?? { inicial: '', final: '' };
    if (id === RANGO_FECHA) {
      if (v.inicial && v.final && v.final < v.inicial)
        return { texto: { clave: T.FECHA_ORDEN }, nivel: 'error' };
      if (v.inicial && v.inicial.slice(0, 4) !== estado.vigencia) {
        return {
          texto: { clave: T.FECHA_FUERA, params: { vigencia: estado.vigencia } },
          nivel: 'aviso',
        };
      }
      return {
        texto: {
          clave: T.FECHA_AYUDA,
          params: { inicial: fechaParaMostrar(v.inicial), final: fechaParaMostrar(v.final) },
        },
        nivel: 'info',
      };
    }
    const rango = this.catalogo.rangos?.find((r) => r.id === id);
    if (this.rangoObligatorio(id) && !v.inicial.trim()) {
      return { texto: { clave: this.claveRangoFaltante(id) }, nivel: 'error' };
    }
    if (rango?.tipo === 'number' && v.inicial && v.final && Number(v.final) < Number(v.inicial)) {
      return { texto: { clave: T.NUMERO_ORDEN }, nivel: 'error' };
    }
    if (rango?.tipo === 'date' && v.inicial && v.final && v.final < v.inicial) {
      return { texto: { clave: T.FECHA_ORDEN }, nivel: 'error' };
    }
    return { texto: { clave: rango?.ayuda ?? '' }, nivel: 'info' };
  }

  /**
   * Clave del mensaje cuando falta un rango obligatorio.
   *
   * @param _id id del rango
   * @returns la clave i18n
   */
  protected claveRangoFaltante(_id: string): string {
    return T.RANGO_FALTA;
  }

  /**
   * Parametros por revisar en cada pestaña: los errores que impiden generar.
   *
   * @param estado parametros actuales
   * @returns el conteo de problemas por pestaña
   */
  problemas(estado: EstadoInforme): Record<PestanaInforme, number> {
    const d = this.definicion;
    const rangos = [RANGO_FECHA, ...(d.rangos ?? [])];
    const filtros = rangos.filter((id) => this.mensajeRango(id, estado).nivel === 'error').length;
    const contenido = (d.selecciones ?? []).filter(
      (id) => !(estado.selecciones[id] ?? []).length,
    ).length;
    const niveles = d.niveles && !estado.niveles.length ? 1 : 0;
    return { filtros, contenido, niveles, firmas: 0, 'vista-previa': 0 };
  }

  /**
   * Busqueda de la lupa de un campo. El contexto del producto la conecta con su selector; sin
   * ella la lupa no se muestra.
   *
   * @param _campo id del campo
   * @param _actual valor actual del campo
   * @returns el valor elegido (o `null` si se cancela), o `null` si el campo no tiene busqueda
   */
  buscar(_campo: string, _actual: string): Observable<string | null> | null {
    return null;
  }

  /**
   * Si el campo tiene lupa en este contexto.
   *
   * @param _campo id del campo
   * @returns si se muestra la lupa
   */
  tieneBusqueda(_campo: string): boolean {
    return false;
  }

  /**
   * Chip de la cabecera del informe con el alcance (p. ej. «Presupuesto de gastos · 2026»).
   *
   * @param estado parametros actuales
   * @returns el texto del chip
   */
  chipDeAlcance(estado: EstadoInforme): TextoInforme {
    return { clave: 'COMMON.INFORMES.CHIP_VIGENCIA', params: { vigencia: estado.vigencia } };
  }

  /**
   * Datos que se pueden filtrar en un nivel de agrupacion.
   *
   * @param _nivel id del nivel
   * @param _estado parametros actuales
   * @returns los datos del nivel
   */
  datosDelNivel(_nivel: string, _estado: EstadoInforme): string[] {
    return [];
  }

  /**
   * Muestra del informe con datos de ejemplo. Sin ella, la pestaña Vista previa no aparece.
   *
   * @param _estado parametros actuales
   * @returns la vista previa, o `null`
   */
  vistaPrevia(_estado: EstadoInforme): VistaPreviaInforme | null {
    return null;
  }

  /**
   * Partes del resumen del pie cuando no hay nada por revisar (nombre, periodo, forma...).
   *
   * @param estado parametros actuales
   * @returns los textos del resumen, en orden
   */
  resumen(estado: EstadoInforme): (TextoInforme | string)[] {
    const f = estado.rangos[RANGO_FECHA];
    const partes: (TextoInforme | string)[] = [
      { clave: this.definicion.nombre },
      `${fechaParaMostrar(f.inicial)} – ${fechaParaMostrar(f.final)}`,
    ];
    if (this.definicion.niveles) {
      const n = estado.niveles.length;
      partes.push({
        clave: n === 1 ? 'COMMON.INFORMES.NIVELES.UNO' : 'COMMON.INFORMES.NIVELES.VARIOS',
        params: { n },
      });
    }
    return partes;
  }
}
